"use client"
import React, { useState, useContext, Suspense } from 'react';
import clsx from 'clsx';
import { SetupLayoutContext } from 'src/app/app/(user-pages)/start/SetupLayoutContext';
import { ContainerSection } from './ContainerSection';
import { Logomark } from '@/components/landing/navigation/Logo';
import { FaCheck } from 'react-icons/fa';
import { FaXmark } from "react-icons/fa6";
const Confetti = React.lazy(() => import('react-dom-confetti'));
const LandingPricingTable = React.lazy(() => import('./LandingPricingTable'));
import '@public/css/pricing.css';
const newBadge = `<span class="rounded-full bg-indigo-600/10 py-1 px-2.5 ml-1 text-xs font-semibold text-indigo-600">NEW</span>`
const betaBadge = `<span class="rounded-full py-1 px-2.5 ml-1 text-xs font-semibold bg-[#f77c08] bg-opacity-10 text-[#f77c08]">BETA</span>`

export let plans = [
  {
    name: 'Starter',
    id: 'basic',
    featured: false,
    priceClassName: 'text-gradient-5 text-transparent bg-clip-text',
    buttonClassName: 'bg-gradient-5 text-white',
    // OLD: price: { Monthly: "99", Yearly: "59", YearlyTotal: "700" }, firstMonthMonthlyPrice: "69"
    price: { Monthly: "57", Yearly: "37", YearlyTotal: "444" },
    firstMonthMonthlyPrice: "27",
    planIDs: ['plan-49', 'plan-350', 'plan-first-month-27', 'plan-basic-57', 'plan-basic-444'],
    description: 'Get started with Instagram growth. Perfect for personal accounts.',
    button: {
      label: 'Try Starter',
      href: '/setup',
    },
    features: [
      '<a href="https://support.upgrow.com/en/articles/8795184-how-many-followers-will-i-get" target="__blank">500 - 800 <span class="underline decoration-dashed" style="text-underline-offset:3px">Real Followers</span> /mo</a>',
      'Basic Targeting Only',
      'No AI Competitor Targeting',
      'No Keesy Boost™',
      'No AI Image Posts',
      'Follower Growth From Day 1',
      'Live Growth Web Dashboard',
      'Real & Organic Followers',
    ],
    logomarkClassName: 'fill-gray-300 hue-rotate-[215deg] saturate-[1.1] brightness-[1.2]',
    checkClassName: 'text-transparent bg-gradient-to-br from-[#7549F4] to-[#FCA6E9] bg-clip-text'
  },
  {
    name: 'Growth',
    id: 'pro',
    featured: true,
    // OLD: price: { Monthly: "149", Yearly: "99", YearlyTotal: "1180" }, firstMonthMonthlyPrice: "99"
    price: { Monthly: "97", Yearly: "67", YearlyTotal: "804" },
    firstMonthMonthlyPrice: "47",
    planIDs: ['plan-99', 'plan-699', 'plan-first-month-47', 'plan-pro-97', 'plan-pro-804'],
    buttonClassName: 'bg-gradient-3 text-white',
    priceClassName: 'text-gradient-3 text-transparent bg-clip-text',
    description: 'Accelerate your Instagram growth with AI-powered targeting. Most popular for creators.',
    button: {
      label: 'Start Growing',
      href: '/setup',
    },
    features: [
      '<a href="https://support.upgrow.com/en/articles/8795184-how-many-followers-will-i-get" target="__blank">1,500 - 2,500 <span class="underline decoration-dashed" style="text-underline-offset:3px">Real Followers</span> /mo</a>',
      `<a href="/perks" target="__blank"><b>$75,000+</b> in <span class="underline decoration-dashed" style="text-underline-offset:3px">Exclusive Perks</span> ${newBadge}</a>`,
      `<b>10</b> <a href="/ai-tools" class="underline decoration-dashed" style="text-underline-offset:3px">AI Image Posts</a> /mo ${betaBadge}`,
      'AI Competitor Targeting',
      'AI Profile Optimization',
      'Keesy Boost™',
      'Priority Chat Support',
      'Full Analytics Dashboard',
      'Real & Organic Followers',
    ],
    logomarkClassName: 'fill-gray-500 hue-rotate-[0deg]',
    checkClassName: 'text-transparent bg-gradient-to-br from-[#F55278] to-[#ffe2be] bg-clip-text saturate-[1.1]'
  },
  {
    name: 'Pro',
    id: 'turbo',
    featured: false,
    // OLD: price: { Monthly: "299", Yearly: "179", YearlyTotal: "2140" }, firstMonthMonthlyPrice: "199"
    price: { Monthly: "197", Yearly: "147", YearlyTotal: "1764" },
    firstMonthMonthlyPrice: "97",
    planIDs: ['plan-249', 'plan-1799', 'plan-first-month-97', 'plan-turbo-197', 'plan-turbo-1764'],
    buttonClassName: 'bg-gradient-4 text-white',
    priceClassName: 'text-gradient-4 text-transparent bg-clip-text',
    description: 'Maximum growth with dedicated support. Perfect for businesses & agencies.',
    button: {
      label: 'Go Pro',
      href: '/setup',
    },
    features: [
      '<a href="https://support.upgrow.com/en/articles/8795184-how-many-followers-will-i-get" target="__blank">3,000 - 4,500+ <span class="underline decoration-dashed" style="text-underline-offset:3px">Real Followers</span> /mo</a>',
      `<a href="/perks" target="__blank"><b>$75,000+</b> in <span class="underline decoration-dashed" style="text-underline-offset:3px">Exclusive Perks</span> ${newBadge}</a>`,
      `<b>30</b> <a href="/ai-tools" class="underline decoration-dashed" style="text-underline-offset:3px">AI Image Posts</a> /mo ${betaBadge}`,
      'AI Competitor Targeting',
      'AI Profile Optimization',
      'Keesy Boost™',
      'Viral Content Library',
      'Dedicated Account Manager',
      'Custom Reporting',
    ],
    logomarkClassName: 'fill-cyan-500 hue-rotate-[280deg]',
    checkClassName: 'text-transparent bg-gradient-to-tr from-[#9B5BE6] via-[#DD5789] to-[#FFCBA0] bg-clip-text saturate-[1.1]'
  },
];

export function Plan({
  name,
  price,
  firstMonthMonthlyPrice,
  description,
  priceClassName,
  buttonClassName,
  button,
  features,
  featured = false,
  activePeriod,
  logomarkClassName,
  checkClassName,
  onPlanSelected,
}) {
  return (
    <div className={`relative landing-card-wrapper ${name == 'Growth' ? 'featured-plan' : ''}`}>
      {name == 'Growth' && (
        <div className={`absolute top-0 bottom-0 left-0 right-0 w-full h-full glow-root hover`} role="button">
          {/* <span className="top-0 bottom-0 left-0 right-0 z-0 mx-auto glow"></span> */}
          <div className='glow-wrapper rounded-3xl'>
            {/* <span>cool</span>Glowing shadows */}
          </div>
        </div>
      )}

      <section className={clsx('relative flex flex-col overflow-hidden rounded-3xl !px-7 md:!px-10 py-12 shadow-lg shadow-gray-900/5',
        featured ? 'order-first bg-gray-900 lg:order-none' : 'landing-card'
      )}>
        {/* Show "Most Popular" badge on Growth tier */}
        {name == 'Growth' && (
          <span className="absolute top-0 right-0 rounded-tr-xl rounded-bl-xl bg-gradient-to-r from-orange-500 to-pink-500 py-1.5 px-3 text-sm font-semibold text-white shadow-lg">
            MOST POPULAR
          </span>
        )}
        <h3 className={clsx('ml-0 flex items-center text-xl font-semibold',
          featured ? 'text-white' : 'text-gray-900'
        )}>
          <Logomark className={clsx('h-6 w-6 flex-none', logomarkClassName)} />
          <span className="ml-4">{name}</span>
          <span className="font-medium whitespace-pre-wrap"> Plan</span>
        </h3>
        <p className={clsx('relative ml-0 mt-5 flex text-[54px] text-left font-bold tracking-[-1px] text-gray-900',
          featured ? 'text-white' : 'text-gray-900'
        )}>
          {price.Monthly === price.Yearly
            ? (price.Monthly)
            : (<>
              <span aria-hidden={activePeriod === 'Yearly'} className={clsx('relative flex transition duration-300', priceClassName,
                activePeriod === 'Yearly' && 'pointer-events-none translate-x-6 select-none opacity-0'
              )}>
                <span className="mr-1 align-top text-2xl leading-[45px]">
                  $
                </span>
                {/* Uncomment this for removing first month price */}
                {/* {price.Monthly} */}
                {firstMonthMonthlyPrice}

                <span className="flex flex-col self-center ml-2.5 text-sm font-normal tracking-normal">
                  <span className="relative font-medium text-gray-300" style={{ fontSize: "22px" }}>
                    <span className='line-through'>${price.Monthly}</span>
                    <span className="px-2 py-1 ml-1.5 text-xs font-semibold text-amber-900 bg-amber-300 rounded-full align-top">LIMITED</span>
                  </span>
                  <span className='mt-0.5 tracking-[-0.2px] text-gray-500'>/month for first month</span>
                </span>


              </span>
              <span aria-hidden={activePeriod === 'Monthly'} className={clsx('absolute flex left-0 top-0 right-0 bottom-0 transition duration-300',
                priceClassName,
                activePeriod === 'Monthly' && 'pointer-events-none -translate-x-6 select-none opacity-0'
              )}>
                <span className="mr-1 align-top text-2xl leading-[45px]">
                  $
                </span>
                {price.Yearly}

                <span className="flex flex-col self-center ml-2.5 text-sm font-normal tracking-normal">
                  <span className="relative font-medium text-gray-300" style={{ fontSize: "22px" }}>
                    <span className='line-through'>${price.Monthly}</span>
                    <span className="px-2 py-1 ml-1.5 text-xs font-semibold text-white bg-green-500 rounded-full align-top">40% OFF</span>
                  </span>
                  <span className='mt-0.5 tracking-[-0.2px] text-gray-500'>/month for 1 year</span>
                </span>

              </span>
            </>)}
        </p>


        <p className={clsx('text-md mt-3 text-left', featured ? 'text-gray-300' : 'text-gray-700')}>
          {description}
        </p>
        <div className="order-last mt-6">
          <b className='relative flex mt-1 mb-4'>
            {name === 'Starter' && ("What's included:")}
            {name === 'Growth' && ('Everything from Starter, plus:')}
            {name === 'Pro' && ('Everything from Growth, plus:')}
          </b>
          <ul role="list" className={clsx('divide-y text-md -my-2 font-medium',
            featured
              ? 'divide-gray-800 text-gray-300'
              : 'divide-gray-200 text-gray-700'
          )}
          >
            {features.map((feature) => (
              <li key={feature} className="relative flex py-1 md:py-2">
                {/* Check if feature starts with "No" */}
                {feature.startsWith('No') ? (
                  <FaXmark className={clsx('my-auto h-5 flex-none', featured ? 'text-white' : 'text-red-500')} style={{ width: "15px" }} />
                ) : (
                  <FaCheck className={clsx('my-auto h-4 w-4 flex-none', featured ? 'text-white' : checkClassName)} />
                )}
                <span className="ml-4" dangerouslySetInnerHTML={{ __html: feature }}></span>
              </li>
            ))}
          </ul>
        </div>

        <a
          href={onPlanSelected ? '#' : button.href}
          className={clsx(
            'mt-6 inline-flex h-[53px] items-center justify-center rounded-xl bg-gray-800 py-3 px-5 text-center text-[17.5px] font-semibold text-white outline-2 outline-offset-2 transition-all duration-300 hover:scale-95 hover:bg-gray-900 active:bg-gray-800 active:text-white/80',
            buttonClassName
          )}
          style={{ textShadow: '0 1px 10px rgba(0,0,0,0.12)' }}
          aria-label={`Get started with the ${name} plan for ${price.Monthly} per month`}
          onClick={(e) => {
            if (onPlanSelected) {
              e.preventDefault();
              onPlanSelected(plans.find((plan) => plan.name === name));
            }
          }}
        >
          {button.label}
        </a>
      </section>
    </div>
  );
}

export default function Pricing({ className, isSetupProcess, selectedPeriod }: { className?: string, isSetupProcess?: boolean, selectedPeriod?: 'Monthly' | 'Yearly' }) {
  const [activePeriod, setActivePeriod] = useState<('Monthly' | 'Yearly')>(selectedPeriod ? selectedPeriod : 'Yearly');
  const [confettiVisible, setConfettiVisible] = useState(false);

  const { setActivePeriod: setActivePeriodContext, setSelectedPlan } = useContext(SetupLayoutContext);
  const handlePlanSelected = (selectedPlan: any) => { setSelectedPlan(selectedPlan) };

  return (
    <ContainerSection className={clsx('section-gradient-1 border-t border-gray-200 py-7 sm:py-10', className, isSetupProcess && '!mt-0 !pt-0 sm:pt-0 !px-0')}
      id="pricing"
      aria-labelledby="pricing-title"
    >

      {!isSetupProcess && (
        <div className="max-w-2xl mx-auto text-center">
          <span className="badge-gradient-1 mb-5 inline-flex rounded-full px-4 py-2 uppercase leading-6 tracking-[0.5px] text-indigo-600">
            <strong className="text-sm font-extrabold text-transparent text-gradient-1 bg-clip-text">
              LIMITED DISCOUNT
            </strong>
          </span>
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
            Honest pricing.{" "}
            <br />
            Grow or money back.
          </h1>
          <p className="mt-6 text-lg leading-7 text-gray-600 lg:text-xl lg:leading-8">
            No bots. No fake followers. Guaranteed growth or it's free!
            <br className='hidden sm:block' />{" "}
            No credit card required. Try it 100% free. Cancel anytime.
          </p>
        </div>
      )}

      <div className={`flex justify-center ${isSetupProcess ? 'mt-1' : 'mt-6'}`}>
        <div className="relative">
          {/* Monthly / Yearly Toggle Switch */}
          <div className="relative mx-auto mt-5 flex self-center rounded-lg bg-gray-100 p-1 py-1.5 sm:mt-10 border-1 border-gray-200">
            {['Monthly', 'Yearly'].map((period: 'Monthly' | 'Yearly') => (
              <button type="button"
                key={period}
                className={clsx(
                  'border text-md relative ml-0.5 w-1/2 whitespace-nowrap rounded-md border-transparent py-2 font-medium transition-colors duration-300 focus:z-10 focus:outline-none sm:w-auto px-8',
                  !(activePeriod === (period == 'Monthly' ? 'Yearly' : 'Monthly'))
                    ? 'border-gray-200 bg-white text-gray-900 shadow-sm'
                    : 'border border-transparent text-gray-700 hover:bg-gray-200'
                )}
                onClick={() => {
                  setActivePeriod(period)
                  if (isSetupProcess && setActivePeriodContext)
                    setActivePeriodContext(period as any)
                }}
              >
                {period}
              </button>
            ))}

            {/* Save % Yearly badge */}
            <div className="absolute top-[-15px] right-[-55px] z-10 flex font-medium h-7 w-[89px] items-center justify-center rounded-2xl bg-green-500 text-sm text-white">
              Save 40%
            </div>
            {/* Yearly save doodle */}
            <span className={clsx(
              'saturate-[1.5] flex flex-row-reverse items-end pt-2 leading-none text-green-500 transition-all duration-300',
              'absolute right-[20px] top-[40px] md:top-full md:left-[70%] md:flex-row left-0 md:right-auto',

              activePeriod === 'Yearly' ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
            )}
            >
              <svg className="transform -scale-x-100 md:scale-x-100" width={25} height={25} viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
                <path d="M28.0919 25.8337C28.4923 25.765 28.8906 26.0698 28.9814 26.5144C29.0723 26.9589 28.8213 27.3748 28.4209 27.4435L28.0919 25.8337ZM8.12688 1.00838C8.52826 0.944434 8.92385 1.25354 9.0105 1.69876L10.4225 8.95421C10.5091 9.39944 10.2541 9.81222 9.85268 9.87616C9.4513 9.9401 9.05571 9.631 8.96906 9.18577L7.71397 2.7365L1.90052 3.66282C1.49914 3.72677 1.10355 3.41768 1.0169 2.97245C0.930253 2.52721 1.18533 2.11445 1.58671 2.05051L8.12688 1.00838ZM28.4209 27.4435C18.8087 29.0908 10.9559 27.0111 6.80609 22.1317C2.56855 17.1494 2.50163 9.67105 7.65909 1.32639L8.90862 2.30272C3.93035 10.3575 4.33505 16.9219 7.86047 21.0669C11.4735 25.315 18.6748 27.4474 28.0919 25.8337L28.4209 27.4435Z" fill="currentcolor" />
              </svg>
              <span className="italic font-medium transform translate-y-1 text-md whitespace-nowrap">
                Get 2 months free!
              </span>
            </span>
          </div>
        </div>
      </div>

      <div className="relative w-1 mx-auto overflow-visible">
        <Confetti
          ref={() => {
            setConfettiVisible(true);
          }}
          active={confettiVisible}
          config={{
            angle: 90,
            spread: 70,
            startVelocity: 45,
            elementCount: 80,
            width: '10px',
            height: '10px',
            colors: ['#a864fd', '#29cdff', '#78ff44', '#ff718d', '#fdff6a'],
            duration: 3e3,
            stagger: 0,
            dragFriction: 0.1,
            random: Math.random,
          }}
        />
      </div>

      <div className="relative grid items-start max-w-2xl grid-cols-1 mx-auto mt-10 gap-x-8 gap-y-10 sm:mt-20 lg:max-w-none lg:grid-cols-3">
        {/* Pricing background mesh gradient */}
        {/* <div className="pointer-events-none absolute top-[-150px] bottom-0 right-0 left-0 animate-[pulse_5s_ease-in-out_2] select-none">
          <div className="pointer-events-none contents">
            <img
              src="https://framerusercontent.com/images/BAEyG5siVI3BMKWQshIIsbDHL4.png"
              alt="Gradient"
              srcSet="https://framerusercontent.com/images/BAEyG5siVI3BMKWQshIIsbDHL4.png?scale-down-to=512 512w, https://framerusercontent.com/images/BAEyG5siVI3BMKWQshIIsbDHL4.png?scale-down-to=1024 1024w, https://framerusercontent.com/images/BAEyG5siVI3BMKWQshIIsbDHL4.png?scale-down-to=2048 2048w, https://framerusercontent.com/images/BAEyG5siVI3BMKWQshIIsbDHL4.png 3200w"
              sizes="1600px"
              className='z-[-9] overflow-visible pointer-events-none select-none block w-full h-full object-center object-cover'
            />
          </div>
        </div> */}

        <h2 style={{ "visibility": "hidden", display: 'none' }}>Choose your Instagram growth plan</h2>
        {plans.map((plan) => {
          const updatedPlan = { ...plan, button: { ...plan.button, label: isSetupProcess ? "Start Your Growth" : plan.button.label } };
          return <Plan key={updatedPlan.name} {...updatedPlan} activePeriod={activePeriod} onPlanSelected={isSetupProcess ? handlePlanSelected : undefined} />
        })}
      </div>

      {/* Bulk discount & referral */}
      {/* <div className='mt-10'>
        <BulkDiscountCalculator />
      </div> */}

      {/* Table comparison */}
      {!isSetupProcess && (
        <div className="hidden mt-10 lg:block">
          <h2 style={{ "visibility": "hidden" }}>Compare Instagram growth services</h2>
          <Suspense fallback={<div className='h-[1202px] w-full text-center' style={{ height: '1202px' }}>Loading...</div>}>
            <LandingPricingTable activePeriod={activePeriod} plans={plans} />
          </Suspense>
        </div>
      )}
    </ContainerSection>
  );
}

// function BulkDiscountCalculator() {
//   // Look at framer.com/pricing for inspiration
//   return (
//     <div className="landing-card-wrapper">
//       <section className="relative flex flex-col px-6 py-8 overflow-hidden shadow-lg landing-card rounded-3xl shadow-gray-900/5">
//         <h3 className="flex items-center text-xl font-semibold text-gray-900">
//           <img
//             src="/img/upgrow-logo-icon.svg"
//             loading="eager"
//             alt="Keesy logo icon"
//             className="h-6 w-6 flex-none fill-gray-300 brightness-[1.2] hue-rotate-[215deg] saturate-[1.1]"
//           />
//           <span className="ml-4">Starter</span>
//           <span className="font-medium whitespace-pre-wrap"> Plan</span>
//         </h3>
//         <p className="relative mt-5 flex text-[54px] font-bold tracking-[-1px] text-gray-900 text-gray-900">
//           <span
//             aria-hidden="false"
//             className="relative text-transparent transition duration-300 text-gradient-5 bg-clip-text"
//           >
//             <span className="mr-1 align-top text-2xl leading-[45px]">$</span>49
//             <span className="text-lg font-medium tracking-normal">/mo</span>
//           </span>
//           <span
//             aria-hidden="true"
//             className="absolute top-0 left-0 text-transparent transition duration-300 -translate-x-6 opacity-0 pointer-events-none select-none text-gradient-5 bg-clip-text"
//           >
//             <span
//               className="mr-1 align-top text-2xl leading-[45px]"
//               style={{ lineHeight: '45px' }}
//             >
//               $
//             </span>
//             29<span className="text-lg font-medium tracking-normal">/mo</span>
//           </span>
//         </p>
//         <p className="mt-3 text-center text-gray-700 text-md">
//           You’re new to investing but want to do it right. Get started for free.
//         </p>
//         <div className="order-last mt-6">
//           <ul
//             role="list"
//             className="-my-2 font-medium text-gray-700 divide-y divide-gray-200 text-md"
//           >
//             <li className="relative flex py-2" style={{ position: 'relative' }}>
//               <svg
//                 stroke="currentColor"
//                 fill="currentColor"
//                 strokeWidth={0}
//                 viewBox="0 0 512 512"
//                 className="flex-none w-4 h-4 my-auto text-cyan-500"
//                 height="1em"
//                 width="1em"
//                 xmlns="http://www.w3.org/2000/svg"
//                 style={{ margin: 'auto 0' }}
//               >
//                 <path d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z" />
//               </svg>
//               <span className="ml-4">Commission-free trading</span>
//             </li>
//             <li className="relative flex py-2">
//               <svg
//                 stroke="currentColor"
//                 fill="currentColor"
//                 strokeWidth={0}
//                 viewBox="0 0 512 512"
//                 className="flex-none w-4 h-4 my-auto text-cyan-500"
//                 height="1em"
//                 width="1em"
//                 xmlns="http://www.w3.org/2000/svg"
//               >
//                 <path d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z" />
//               </svg>
//               <span className="ml-4">Multi-layered encryption</span>
//             </li>
//             <li className="relative flex py-2">
//               <svg
//                 stroke="currentColor"
//                 fill="currentColor"
//                 strokeWidth={0}
//                 viewBox="0 0 512 512"
//                 className="flex-none w-4 h-4 my-auto text-cyan-500"
//                 height="1em"
//                 width="1em"
//                 xmlns="http://www.w3.org/2000/svg"
//               >
//                 <path d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z" />
//               </svg>
//               <span className="ml-4">One tip every day</span>
//             </li>
//             <li className="relative flex py-2">
//               <svg
//                 stroke="currentColor"
//                 fill="currentColor"
//                 strokeWidth={0}
//                 viewBox="0 0 512 512"
//                 className="flex-none w-4 h-4 my-auto text-cyan-500"
//                 height="1em"
//                 width="1em"
//                 xmlns="http://www.w3.org/2000/svg"
//               >
//                 <path d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z" />
//               </svg>
//               <span className="ml-4">Invest up to $1,500 each month</span>
//             </li>
//           </ul>
//         </div>
//         <a
//           className="bg-gradient-5 mt-6 inline-flex h-[53px] items-center justify-center rounded-xl bg-gray-800 py-3 px-5 text-center text-[17.5px] font-semibold text-white text-white outline-2 outline-offset-2 transition-all duration-300 hover:scale-95 hover:bg-gray-900 active:bg-gray-800 active:text-white/80"
//           aria-label="Get started with the Starter plan for [object Object]"
//           href="/setup"
//         >
//           Start your free trial
//         </a>
//       </section>
//     </div>
//   );
// }

